import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Alert,
  Grid,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Collapse,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import SportsTennisIcon from '@mui/icons-material/SportsTennis';
import SportsIcon from '@mui/icons-material/Sports';
import EventIcon from '@mui/icons-material/Event';
import CancelIcon from '@mui/icons-material/Cancel';
import RefreshIcon from '@mui/icons-material/Refresh';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BlockIcon from '@mui/icons-material/Block';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PersonIcon from '@mui/icons-material/Person';
import SchoolIcon from '@mui/icons-material/School';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import GroupIcon from '@mui/icons-material/Group';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import courtService from '../api/services/courtService';
import memberService from '../api/services/memberService';
import nonMemberService from '../api/services/nonMemberService';
import proBookingService from '../api/services/proBookingService';
import programService from '../api/services/programService';
import { getErrorMessage } from '../api/axiosConfig';
import {
  Court,
  CourtReservation,
  CourtOperatingHours,
  ReservationStatus,
  ReservationType,
  ReservationPlayer,
  Member,
  NonMember,
  Pro,
  ProBooking,
  ProSessionType,
  ProAvailabilitySchedule,
  ProBookingParticipant,
  Program,
  ProgramEnrollment,
} from '../types';

const CourtBookingPage: React.FC = () => {
  // Get current user from Redux
  const currentUser = useSelector((state: RootState) => state.auth.user);
  const isStaff = currentUser?.role === 'STAFF' || currentUser?.role === 'ADMIN' || currentUser?.role === 'SUPERUSER';

  // State
  const [courts, setCourts] = useState<Court[]>([]);
  const [myReservations, setMyReservations] = useState<CourtReservation[]>([]);
  const [operatingHours, setOperatingHours] = useState<CourtOperatingHours[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Filters
  const [courtType, setCourtType] = useState<string>('all');
  const [selectedDate, setSelectedDate] = useState<string>(
    new Date().toISOString().split('T')[0]
  );
  const [selectedDuration, setSelectedDuration] = useState<number>(60);

  // Booking Dialog
  const [bookingDialogOpen, setBookingDialogOpen] = useState(false);
  const [selectedCourt, setSelectedCourt] = useState<Court | null>(null);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<string | null>(null);
  const [reservationType, setReservationType] = useState<ReservationType>(ReservationType.SINGLES);
  const [selectedPlayers, setSelectedPlayers] = useState<ReservationPlayer[]>([]);
  const [notes, setNotes] = useState<string>('');
  const [bookingLoading, setBookingLoading] = useState(false);

  // Player search state
  const [memberSearchResults, setMemberSearchResults] = useState<Member[]>([]);
  const [nonMemberSearchResults, setNonMemberSearchResults] = useState<NonMember[]>([]);
  const [playerSearchQuery, setPlayerSearchQuery] = useState<string>('');
  const [searchingPlayers, setSearchingPlayers] = useState(false);

  // Cancel Dialog
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelReservationId, setCancelReservationId] = useState<string | null>(null);
  const [cancelReason, setCancelReason] = useState<string>('');

  // Schedule state
  const [courtSchedules, setCourtSchedules] = useState<{ [courtId: string]: CourtReservation[] }>({});
  const [loadingSchedules, setLoadingSchedules] = useState(false);

  // Tab state
  const [tabValue, setTabValue] = useState(0);

  // Expanded reservation rows
  const [expandedReservations, setExpandedReservations] = useState<Set<string>>(new Set());

  // Pagination for past reservations
  const [pastReservationsLimit, setPastReservationsLimit] = useState(10);
  const PAST_RESERVATIONS_PAGE_SIZE = 10;

  // ==================== Pro Booking State ====================
  const [pros, setPros] = useState<Pro[]>([]);
  const [selectedPro, setSelectedPro] = useState<Pro | null>(null);
  const [proAvailability, setProAvailability] = useState<ProAvailabilitySchedule | null>(null);
  const [proSessionTypes, setProSessionTypes] = useState<ProSessionType[]>([]);
  const [myProBookings, setMyProBookings] = useState<ProBooking[]>([]);
  const [loadingPros, setLoadingPros] = useState(false);
  const [loadingProAvailability, setLoadingProAvailability] = useState(false);
  const [proBookingDate, setProBookingDate] = useState<string>(
    new Date().toISOString().split('T')[0]
  );

  // Pro Booking Dialog
  const [proBookingDialogOpen, setProBookingDialogOpen] = useState(false);
  const [selectedProTimeSlot, setSelectedProTimeSlot] = useState<string | null>(null);
  const [selectedSessionType, setSelectedSessionType] = useState<ProSessionType | null>(null);
  const [proBookingParticipants, setProBookingParticipants] = useState<ProBookingParticipant[]>([]);
  const [proBookingNotes, setProBookingNotes] = useState<string>('');
  const [proBookingLoading, setProBookingLoading] = useState(false);

  // ==================== Programs State ====================
  const [programs, setPrograms] = useState<Program[]>([]);
  const [myEnrollments, setMyEnrollments] = useState<ProgramEnrollment[]>([]);
  const [loadingPrograms, setLoadingPrograms] = useState(false);
  const [selectedProgram, setSelectedProgram] = useState<Program | null>(null);
  const [programDetailDialogOpen, setProgramDetailDialogOpen] = useState(false);
  const [enrollingInProgram, setEnrollingInProgram] = useState(false);

  // ==================== Staff Management State ====================
  const [myAvailability, setMyAvailability] = useState<any[]>([]);
  const [mySessionTypes, setMySessionTypes] = useState<ProSessionType[]>([]);
  const [staffPrograms, setStaffPrograms] = useState<Program[]>([]);
  const [loadingStaffData, setLoadingStaffData] = useState(false);

  // Availability Dialog
  const [availabilityDialogOpen, setAvailabilityDialogOpen] = useState(false);
  const [editingAvailability, setEditingAvailability] = useState<any>(null);
  const [availabilitySlots, setAvailabilitySlots] = useState<any[]>([]);
  const [newSlot, setNewSlot] = useState({
    day_of_week: 0,
    start_time: '09:00',
    end_time: '17:00',
  });

  // Session Type Dialog
  const [sessionTypeDialogOpen, setSessionTypeDialogOpen] = useState(false);
  const [editingSessionType, setEditingSessionType] = useState<ProSessionType | null>(null);
  const [sessionTypeForm, setSessionTypeForm] = useState({
    name: '',
    description: '',
    duration_minutes: 60,
    max_participants: 1,
    price_member: 0,
    price_non_member: 0,
  });

  // Program Dialog
  const [programDialogOpen, setProgramDialogOpen] = useState(false);
  const [editingProgram, setEditingProgram] = useState<Program | null>(null);
  const [programForm, setProgramForm] = useState({
    name: '',
    description: '',
    program_type: 'tennis',
    price_member: 0,
    price_non_member: 0,
    max_participants: 10,
    min_participants: 4,
    start_date: '',
    end_date: '',
    recurrence_days: [] as number[],
    recurrence_time: '09:00',
    recurrence_duration: 60,
  });

  const toggleReservationExpanded = (reservationId: string) => {
    setExpandedReservations((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(reservationId)) {
        newSet.delete(reservationId);
      } else {
        newSet.add(reservationId);
      }
      return newSet;
    });
  };

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (courts.length > 0) {
      loadSchedulesForDate();
    }
  }, [selectedDate, courts]);

  // Load pros data when switching to Book a Pro tab
  useEffect(() => {
    if (tabValue === 2 && pros.length === 0) {
      loadPros();
      loadMyProBookings();
    }
  }, [tabValue]);

  // Load programs data when switching to Programs tab
  useEffect(() => {
    if (tabValue === 3 && programs.length === 0) {
      loadPrograms();
    }
  }, [tabValue]);

  // Load staff data when switching to Staff Management tab
  useEffect(() => {
    if (tabValue === 4 && isStaff && staffPrograms.length === 0) {
      loadStaffData();
    }
  }, [tabValue, isStaff]);

  const loadInitialData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [courtsData, reservationsData, hoursData] = await Promise.all([
        courtService.getAllCourts(),
        courtService.getMyReservations(true),
        courtService.getOperatingHours(),
      ]);
      setCourts(courtsData);
      setMyReservations(reservationsData);
      setOperatingHours(hoursData);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const loadSchedulesForDate = async () => {
    setLoadingSchedules(true);
    try {
      const schedules: { [courtId: string]: CourtReservation[] } = {};
      const filteredCourts = getFilteredCourts();

      await Promise.all(
        filteredCourts.map(async (court) => {
          try {
            const schedule = await courtService.getCourtSchedule(court.id, selectedDate);
            schedules[court.id] = schedule.reservations;
          } catch {
            schedules[court.id] = [];
          }
        })
      );

      setCourtSchedules(schedules);
    } catch (err) {
      console.error('Error loading schedules:', err);
    } finally {
      setLoadingSchedules(false);
    }
  };

  const getFilteredCourts = () => {
    if (courtType === 'all') return courts;
    if (courtType === 'ball_machine') {
      // Ball machine is only available on Court 6
      return courts.filter((court) => court.court_number === 6);
    }
    if (courtType === 'clay') {
      // Filter by clay surface
      return courts.filter((court) => court.surface === 'clay');
    }
    return courts.filter((court) => court.court_type === courtType);
  };

  const getOperatingHoursForDay = (): CourtOperatingHours | null => {
    const date = new Date(selectedDate);
    const dayOfWeek = date.getDay();
    const adjustedDay = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    return operatingHours.find((h) => h.day_of_week === adjustedDay) || null;
  };

  const generateTimeSlots = (): string[] => {
    const hours = getOperatingHoursForDay();
    if (!hours || hours.is_closed) return [];
    return courtService.generateTimeSlots(selectedDate, hours, selectedDuration);
  };

  const isSlotAvailable = (courtId: string, startTime: string): boolean => {
    const reservations = courtSchedules[courtId] || [];
    const slotStart = new Date(startTime).getTime();
    const slotEnd = slotStart + selectedDuration * 60 * 1000;

    return !reservations.some((res) => {
      if (res.status === ReservationStatus.CANCELLED) return false;
      const resStart = new Date(res.start_time).getTime();
      const resEnd = new Date(res.end_time).getTime();
      return slotStart < resEnd && slotEnd > resStart;
    });
  };

  const handleOpenBookingDialog = (court: Court, timeSlot: string) => {
    setSelectedCourt(court);
    setSelectedTimeSlot(timeSlot);
    setReservationType(ReservationType.SINGLES);
    setSelectedPlayers([]);
    setNotes('');
    setPlayerSearchQuery('');
    setMemberSearchResults([]);
    setNonMemberSearchResults([]);
    setBookingDialogOpen(true);
  };

  const handleCloseBookingDialog = () => {
    setBookingDialogOpen(false);
    setSelectedCourt(null);
    setSelectedTimeSlot(null);
    setSelectedPlayers([]);
    setPlayerSearchQuery('');
  };

  // Search for players (members and non-members)
  const handlePlayerSearch = async (query: string) => {
    setPlayerSearchQuery(query);
    if (query.length < 2) {
      setMemberSearchResults([]);
      setNonMemberSearchResults([]);
      return;
    }

    setSearchingPlayers(true);
    try {
      // Search members using the booking search endpoint (available to all authenticated users)
      const membersResponse = await memberService.searchMembersForBooking(query);
      setMemberSearchResults(membersResponse.data || []);

      // Search non-members using the booking search endpoint (available to all authenticated users)
      const nonMembersResponse = await nonMemberService.searchNonMembersForBooking(query);
      setNonMemberSearchResults(nonMembersResponse.data || []);
    } catch (err) {
      console.error('Error searching players:', err);
    } finally {
      setSearchingPlayers(false);
    }
  };

  const addPlayer = (player: Member | NonMember, type: 'member' | 'non_member') => {
    // Check if already added
    if (selectedPlayers.some((p) => p.id === player.id)) {
      return;
    }

    const newPlayer: ReservationPlayer = {
      id: player.id,
      type,
      name: `${player.first_name} ${player.last_name}`,
    };
    setSelectedPlayers([...selectedPlayers, newPlayer]);
    setPlayerSearchQuery('');
    setMemberSearchResults([]);
    setNonMemberSearchResults([]);
  };

  const removePlayer = (playerId: string) => {
    setSelectedPlayers(selectedPlayers.filter((p) => p.id !== playerId));
  };

  // Get reservation type options based on court
  const getReservationTypeOptions = () => {
    const options = [
      { value: ReservationType.SINGLES, label: 'Singles' },
      { value: ReservationType.DOUBLES, label: 'Doubles' },
    ];
    // Ball machine only available on Court 6
    if (selectedCourt?.court_number === 6) {
      options.push({ value: ReservationType.BALL_MACHINE, label: 'Ball Machine' });
    }
    return options;
  };

  const handleCreateReservation = async () => {
    if (!selectedCourt || !selectedTimeSlot) return;

    setBookingLoading(true);
    setError(null);

    try {
      await courtService.createReservation({
        court_id: selectedCourt.id,
        start_time: selectedTimeSlot,
        duration_minutes: selectedDuration,
        reservation_type: reservationType,
        players: selectedPlayers.length > 0 ? selectedPlayers : undefined,
        notes: notes || undefined,
      });

      setSuccess('Reservation created successfully!');
      handleCloseBookingDialog();
      await loadInitialData();
      await loadSchedulesForDate();

      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setBookingLoading(false);
    }
  };

  const handleOpenCancelDialog = (reservationId: string) => {
    setCancelReservationId(reservationId);
    setCancelReason('');
    setCancelDialogOpen(true);
  };

  const handleCloseCancelDialog = () => {
    setCancelDialogOpen(false);
    setCancelReservationId(null);
  };

  const handleCancelReservation = async () => {
    if (!cancelReservationId) return;

    setBookingLoading(true);
    setError(null);

    try {
      await courtService.cancelReservation(cancelReservationId, cancelReason || undefined);
      setSuccess('Reservation cancelled successfully!');
      handleCloseCancelDialog();
      await loadInitialData();
      await loadSchedulesForDate();

      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setBookingLoading(false);
    }
  };

  const navigateDate = (days: number) => {
    const date = new Date(selectedDate);
    date.setDate(date.getDate() + days);
    setSelectedDate(date.toISOString().split('T')[0]);
  };

  const formatTimeSlot = (isoString: string) => {
    return courtService.formatTime(isoString);
  };

  // ==================== Pro Booking Functions ====================

  const loadPros = async () => {
    setLoadingPros(true);
    try {
      const prosData = await proBookingService.getAllPros();
      setPros(prosData);
    } catch (err) {
      console.error('Error loading pros:', err);
    } finally {
      setLoadingPros(false);
    }
  };

  const loadProAvailability = async (proId: string, date: string) => {
    setLoadingProAvailability(true);
    try {
      const availability = await proBookingService.getProAvailability(proId, date);
      setProAvailability(availability);
    } catch (err) {
      console.error('Error loading pro availability:', err);
      setProAvailability(null);
    } finally {
      setLoadingProAvailability(false);
    }
  };

  const loadProSessionTypes = async (proId: string) => {
    try {
      const types = await proBookingService.getProSessionTypes(proId);
      setProSessionTypes(types);
      if (types.length > 0) {
        setSelectedSessionType(types[0]);
      }
    } catch (err) {
      console.error('Error loading session types:', err);
    }
  };

  const loadMyProBookings = async () => {
    try {
      const bookings = await proBookingService.getMyBookings(true);
      setMyProBookings(bookings);
    } catch (err) {
      console.error('Error loading pro bookings:', err);
    }
  };

  const handleSelectPro = async (pro: Pro) => {
    setSelectedPro(pro);
    await Promise.all([
      loadProAvailability(pro.id, proBookingDate),
      loadProSessionTypes(pro.id),
    ]);
  };

  const handleProDateChange = async (date: string) => {
    setProBookingDate(date);
    if (selectedPro) {
      await loadProAvailability(selectedPro.id, date);
    }
  };

  const handleOpenProBookingDialog = (timeSlot: string) => {
    setSelectedProTimeSlot(timeSlot);
    setProBookingParticipants([]);
    setProBookingNotes('');
    setProBookingDialogOpen(true);
  };

  const handleCloseProBookingDialog = () => {
    setProBookingDialogOpen(false);
    setSelectedProTimeSlot(null);
  };

  const handleCreateProBooking = async () => {
    if (!selectedPro || !selectedProTimeSlot || !selectedSessionType) return;

    setProBookingLoading(true);
    setError(null);

    try {
      await proBookingService.createBooking({
        pro_id: selectedPro.id,
        session_type_id: selectedSessionType.id,
        start_time: selectedProTimeSlot,
        participants: proBookingParticipants.length > 0 ? proBookingParticipants : undefined,
        notes: proBookingNotes || undefined,
      });

      setSuccess('Pro lesson booked successfully!');
      handleCloseProBookingDialog();
      await loadMyProBookings();
      if (selectedPro) {
        await loadProAvailability(selectedPro.id, proBookingDate);
      }

      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setProBookingLoading(false);
    }
  };

  const handleCancelProBooking = async (bookingId: string) => {
    try {
      await proBookingService.cancelBooking(bookingId);
      setSuccess('Pro booking cancelled successfully!');
      await loadMyProBookings();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(getErrorMessage(err));
    }
  };

  // ==================== Programs Functions ====================

  const loadPrograms = async () => {
    setLoadingPrograms(true);
    try {
      const [programsData, enrollmentsData] = await Promise.all([
        programService.getPublishedPrograms(),
        programService.getMyEnrollments(true),
      ]);
      setPrograms(programsData);
      setMyEnrollments(enrollmentsData);
    } catch (err) {
      console.error('Error loading programs:', err);
    } finally {
      setLoadingPrograms(false);
    }
  };

  const handleViewProgramDetails = (program: Program) => {
    setSelectedProgram(program);
    setProgramDetailDialogOpen(true);
  };

  const handleCloseProgramDetails = () => {
    setProgramDetailDialogOpen(false);
    setSelectedProgram(null);
  };

  const handleEnrollInProgram = async (programId: string) => {
    setEnrollingInProgram(true);
    setError(null);

    try {
      await programService.enrollInProgram(programId);
      setSuccess('Successfully enrolled in program!');
      await loadPrograms();
      handleCloseProgramDetails();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setEnrollingInProgram(false);
    }
  };

  const handleCancelEnrollment = async (enrollmentId: string) => {
    try {
      await programService.cancelEnrollment(enrollmentId);
      setSuccess('Enrollment cancelled successfully!');
      await loadPrograms();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(getErrorMessage(err));
    }
  };

  const isEnrolledInProgram = (programId: string): boolean => {
    return myEnrollments.some(
      (e) => e.program_id === programId && e.status === 'registered'
    );
  };

  const getUpcomingProBookings = () => {
    const now = new Date();
    return myProBookings
      .filter((booking) => new Date(booking.start_time) >= now && booking.status !== 'cancelled')
      .sort((a, b) => new Date(a.start_time).getTime() - new Date(b.start_time).getTime());
  };

  const getActiveEnrollments = () => {
    return myEnrollments.filter((e) => e.status === 'registered' || e.status === 'waitlisted');
  };

  // ==================== Staff Management Functions ====================

  const loadStaffData = async () => {
    if (!isStaff) return;
    setLoadingStaffData(true);
    try {
      const [availabilityData, sessionTypesData, programsData] = await Promise.all([
        proBookingService.getMyBookingsAsPro().catch(() => []),
        proBookingService.getProSessionTypes(currentUser?.id || '').catch(() => []),
        programService.getAllPrograms(true).catch(() => []),
      ]);
      setMyAvailability(availabilityData);
      setMySessionTypes(sessionTypesData);
      setStaffPrograms(programsData);
    } catch (err) {
      console.error('Error loading staff data:', err);
    } finally {
      setLoadingStaffData(false);
    }
  };

  const handleCreateSessionType = async () => {
    try {
      if (editingSessionType) {
        await proBookingService.updateSessionType(editingSessionType.id, sessionTypeForm);
        setSuccess('Session type updated successfully!');
      } else {
        await proBookingService.createSessionType(sessionTypeForm);
        setSuccess('Session type created successfully!');
      }
      setSessionTypeDialogOpen(false);
      setEditingSessionType(null);
      setSessionTypeForm({
        name: '',
        description: '',
        duration_minutes: 60,
        max_participants: 1,
        price_member: 0,
        price_non_member: 0,
      });
      loadStaffData();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(getErrorMessage(err));
    }
  };

  const handleEditSessionType = (sessionType: ProSessionType) => {
    setEditingSessionType(sessionType);
    setSessionTypeForm({
      name: sessionType.name,
      description: sessionType.description || '',
      duration_minutes: sessionType.duration_minutes,
      max_participants: sessionType.max_participants,
      price_member: sessionType.price_member,
      price_non_member: sessionType.price_non_member || 0,
    });
    setSessionTypeDialogOpen(true);
  };

  const handleDeleteSessionType = async (sessionTypeId: string) => {
    if (!window.confirm('Are you sure you want to delete this session type?')) return;
    try {
      await proBookingService.deleteSessionType(sessionTypeId);
      setSuccess('Session type deleted successfully!');
      loadStaffData();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(getErrorMessage(err));
    }
  };

  const handleCreateProgram = async () => {
    try {
      const programData = {
        name: programForm.name,
        description: programForm.description,
        program_type: programForm.program_type,
        instructor_id: currentUser?.id,
        price_member: programForm.price_member,
        price_non_member: programForm.price_non_member || undefined,
        max_participants: programForm.max_participants,
        min_participants: programForm.min_participants || undefined,
        start_date: programForm.start_date,
        end_date: programForm.end_date,
        recurrence_pattern: programForm.recurrence_days.length > 0 ? {
          days: programForm.recurrence_days,
          time: programForm.recurrence_time,
          duration: programForm.recurrence_duration,
        } : undefined,
      };
      if (editingProgram) {
        await programService.updateProgram(editingProgram.id, programData as any);
        setSuccess('Program updated successfully!');
      } else {
        await programService.createProgram(programData as any);
        setSuccess('Program created successfully!');
      }
      setProgramDialogOpen(false);
      setEditingProgram(null);
      setProgramForm({
        name: '',
        description: '',
        program_type: 'tennis',
        price_member: 0,
        price_non_member: 0,
        max_participants: 10,
        min_participants: 4,
        start_date: '',
        end_date: '',
        recurrence_days: [],
        recurrence_time: '09:00',
        recurrence_duration: 60,
      });
      loadStaffData();
      loadPrograms();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(getErrorMessage(err));
    }
  };

  const handleEditProgram = (program: Program) => {
    setEditingProgram(program);
    setProgramForm({
      name: program.name,
      description: program.description || '',
      program_type: program.program_type,
      price_member: program.price_member,
      price_non_member: program.price_non_member || 0,
      max_participants: program.max_participants,
      min_participants: program.min_participants || 4,
      start_date: program.start_date,
      end_date: program.end_date,
      recurrence_days: program.recurrence_pattern?.days || [],
      recurrence_time: program.recurrence_pattern?.time || '09:00',
      recurrence_duration: program.recurrence_pattern?.duration || 60,
    });
    setProgramDialogOpen(true);
  };

  const handleDeleteProgram = async (programId: string) => {
    if (!window.confirm('Are you sure you want to cancel this program?')) return;
    try {
      await programService.cancelProgram(programId);
      setSuccess('Program cancelled successfully!');
      loadStaffData();
      loadPrograms();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(getErrorMessage(err));
    }
  };

  const handleUpdateAvailability = async (availability: any[]) => {
    try {
      await proBookingService.updateMyAvailability(availability);
      setSuccess('Availability updated successfully!');
      setAvailabilityDialogOpen(false);
      loadStaffData();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(getErrorMessage(err));
    }
  };

  const handleOpenAvailabilityDialog = () => {
    // Copy existing availability to local state for editing
    setAvailabilitySlots([...myAvailability]);
    setAvailabilityDialogOpen(true);
  };

  const handleAddAvailabilitySlot = () => {
    const newSlotData = {
      id: `temp-${Date.now()}`,
      day_of_week: newSlot.day_of_week,
      start_time: newSlot.start_time,
      end_time: newSlot.end_time,
      is_active: true,
    };
    setAvailabilitySlots([...availabilitySlots, newSlotData]);
    setNewSlot({ day_of_week: 0, start_time: '09:00', end_time: '17:00' });
  };

  const handleRemoveAvailabilitySlot = (index: number) => {
    const updated = availabilitySlots.filter((_, i) => i !== index);
    setAvailabilitySlots(updated);
  };

  const handleSaveAvailability = async () => {
    try {
      await proBookingService.updateMyAvailability(availabilitySlots);
      setSuccess('Availability updated successfully!');
      setAvailabilityDialogOpen(false);
      loadStaffData();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(getErrorMessage(err));
    }
  };

  const dayLabels = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  const getUpcomingReservations = () => {
    const now = new Date();
    return myReservations
      .filter(
        (res) =>
          res.status === ReservationStatus.CONFIRMED &&
          new Date(res.start_time) >= now
      )
      .sort((a, b) => new Date(a.start_time).getTime() - new Date(b.start_time).getTime());
  };

  const getPastReservations = () => {
    const now = new Date();
    return myReservations
      .filter(
        (res) =>
          res.status !== ReservationStatus.CONFIRMED ||
          new Date(res.start_time) < now
      )
      .sort((a, b) => new Date(b.start_time).getTime() - new Date(a.start_time).getTime());
  };

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          <SportsTennisIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Court Reservations
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Book tennis and pickleball courts
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Tabs
        value={tabValue}
        onChange={(_, v) => setTabValue(v)}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        sx={{ mb: 3 }}
      >
        <Tab label="Book a Court" />
        <Tab label={`My Reservations (${getUpcomingReservations().length})`} />
        <Tab label="Book a Pro" icon={<PersonIcon />} iconPosition="start" />
        <Tab label="Programs" icon={<SchoolIcon />} iconPosition="start" />
        {isStaff && <Tab label="Staff Management" icon={<EventIcon />} iconPosition="start" />}
      </Tabs>

      {/* Book a Court Tab */}
      {tabValue === 0 && (
        <>
          {/* Filters */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Grid container spacing={2} alignItems="center">
                <Grid size={{ xs: 12, sm: 3 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Court Type</InputLabel>
                    <Select
                      value={courtType}
                      label="Court Type"
                      onChange={(e) => setCourtType(e.target.value)}
                    >
                      <MenuItem value="all">All Courts</MenuItem>
                      <MenuItem value="tennis">Tennis</MenuItem>
                      <MenuItem value="pickleball">Pickleball</MenuItem>
                      <MenuItem value="ball_machine">Ball Machine</MenuItem>
                      <MenuItem value="clay">Clay</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid size={{ xs: 12, sm: 3 }}>
                  <Box display="flex" alignItems="center">
                    <IconButton onClick={() => navigateDate(-1)} size="small">
                      <ChevronLeftIcon />
                    </IconButton>
                    <TextField
                      type="date"
                      size="small"
                      value={selectedDate}
                      onChange={(e) => setSelectedDate(e.target.value)}
                      inputProps={{
                        min: new Date().toISOString().split('T')[0],
                      }}
                      fullWidth
                    />
                    <IconButton onClick={() => navigateDate(1)} size="small">
                      <ChevronRightIcon />
                    </IconButton>
                  </Box>
                </Grid>

                <Grid size={{ xs: 12, sm: 3 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Duration</InputLabel>
                    <Select
                      value={selectedDuration}
                      label="Duration"
                      onChange={(e) => setSelectedDuration(Number(e.target.value))}
                    >
                      <MenuItem value={30}>30 minutes</MenuItem>
                      <MenuItem value={60}>1 hour</MenuItem>
                      <MenuItem value={90}>1.5 hours</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid size={{ xs: 12, sm: 3 }}>
                  <Button
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={() => loadSchedulesForDate()}
                    disabled={loadingSchedules}
                    fullWidth
                  >
                    Refresh
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Operating Hours Info */}
          {getOperatingHoursForDay() && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <strong>Hours:</strong>{' '}
              {getOperatingHoursForDay()!.is_closed
                ? 'Closed'
                : `${getOperatingHoursForDay()!.open_time} - ${getOperatingHoursForDay()!.close_time}`}
            </Alert>
          )}

          {/* Court Schedule Grid */}
          {loadingSchedules ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper} sx={{ width: 'fit-content', maxWidth: '100%' }}>
              <Table size="small" sx={{ tableLayout: 'auto' }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', minWidth: 120 }}>Time</TableCell>
                    {getFilteredCourts().map((court) => (
                      <TableCell
                        key={court.id}
                        align="center"
                        sx={{ fontWeight: 'bold', minWidth: 100 }}
                      >
                        <Stack alignItems="center" spacing={0.5}>
                          {court.court_type === 'tennis' ? (
                            <SportsTennisIcon fontSize="small" />
                          ) : (
                            <SportsIcon fontSize="small" />
                          )}
                          <Typography variant="caption">{court.name}</Typography>
                          <Chip
                            label={court.surface}
                            size="small"
                            color={court.surface === 'clay' ? 'warning' : 'default'}
                            sx={{ fontSize: '0.65rem', height: 18 }}
                          />
                        </Stack>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {generateTimeSlots().length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={getFilteredCourts().length + 1} align="center">
                        <Typography color="text.secondary" py={2}>
                          No available time slots for this date
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    generateTimeSlots().map((slot) => (
                      <TableRow key={slot} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {formatTimeSlot(slot)}
                          </Typography>
                        </TableCell>
                        {getFilteredCourts().map((court) => {
                          const available = isSlotAvailable(court.id, slot);
                          const isPast = new Date(slot) < new Date();

                          return (
                            <TableCell key={court.id} align="center">
                              {isPast ? (
                                <Chip
                                  label="Past"
                                  size="small"
                                  color="default"
                                  sx={{ opacity: 0.5 }}
                                />
                              ) : available ? (
                                <Button
                                  variant="contained"
                                  color="success"
                                  size="small"
                                  onClick={() => handleOpenBookingDialog(court, slot)}
                                  sx={{ minWidth: 80 }}
                                >
                                  Book
                                </Button>
                              ) : (
                                <Chip
                                  label="Booked"
                                  size="small"
                                  color="error"
                                  icon={<BlockIcon />}
                                />
                              )}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}

      {/* My Reservations Tab */}
      {tabValue === 1 && (
        <>
          {/* Upcoming Reservations */}
          <Typography variant="h6" gutterBottom>
            Upcoming Reservations
          </Typography>

          {getUpcomingReservations().length === 0 ? (
            <Alert severity="info" sx={{ mb: 3 }}>
              You have no upcoming reservations.
            </Alert>
          ) : (
            <TableContainer component={Paper} sx={{ mb: 4 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell width={50} />
                    <TableCell>Court</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Time</TableCell>
                    <TableCell>Duration</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {getUpcomingReservations().map((res) => (
                    <React.Fragment key={res.id}>
                      <TableRow hover sx={{ '& > *': { borderBottom: 'unset' } }}>
                        <TableCell>
                          <IconButton
                            size="small"
                            onClick={() => toggleReservationExpanded(res.id)}
                          >
                            {expandedReservations.has(res.id) ? (
                              <KeyboardArrowUpIcon />
                            ) : (
                              <KeyboardArrowDownIcon />
                            )}
                          </IconButton>
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            {res.court?.court_type === 'tennis' ? (
                              <SportsTennisIcon fontSize="small" />
                            ) : (
                              <SportsIcon fontSize="small" />
                            )}
                            <Typography>{res.court?.name || 'Unknown'}</Typography>
                          </Stack>
                        </TableCell>
                        <TableCell>{courtService.formatDate(res.start_time)}</TableCell>
                        <TableCell>
                          {courtService.formatTime(res.start_time)} -{' '}
                          {courtService.formatTime(res.end_time)}
                        </TableCell>
                        <TableCell>{courtService.getDurationLabel(res.duration_minutes)}</TableCell>
                        <TableCell>
                          <Chip
                            label={res.reservation_type?.replace('_', ' ') || 'Singles'}
                            size="small"
                            color={res.reservation_type === 'ball_machine' ? 'secondary' : 'default'}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="Cancel Reservation">
                            <IconButton
                              color="error"
                              size="small"
                              onClick={() => handleOpenCancelDialog(res.id)}
                            >
                              <CancelIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
                          <Collapse in={expandedReservations.has(res.id)} timeout="auto" unmountOnExit>
                            <Box sx={{ margin: 2 }}>
                              <Typography variant="subtitle2" gutterBottom>
                                Reservation Details
                              </Typography>
                              <Grid container spacing={2}>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                  <Typography variant="body2" color="text.secondary">
                                    <strong>Players:</strong>
                                  </Typography>
                                  {res.players && res.players.length > 0 ? (
                                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                                      {res.players.map((player, index) => (
                                        <Chip
                                          key={index}
                                          label={player.name}
                                          size="small"
                                          color={player.type === 'member' ? 'primary' : 'secondary'}
                                          variant="outlined"
                                        />
                                      ))}
                                    </Stack>
                                  ) : (
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                      No players added
                                    </Typography>
                                  )}
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                  <Typography variant="body2" color="text.secondary">
                                    <strong>Notes:</strong>
                                  </Typography>
                                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                                    {res.notes || 'No notes'}
                                  </Typography>
                                </Grid>
                              </Grid>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Past Reservations */}
          <Typography variant="h6" gutterBottom>
            Past Reservations
          </Typography>

          {getPastReservations().length === 0 ? (
            <Alert severity="info">No past reservations.</Alert>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Showing {Math.min(pastReservationsLimit, getPastReservations().length)} of {getPastReservations().length} past reservations
              </Typography>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell width={50} />
                      <TableCell>Court</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Time</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {getPastReservations().slice(0, pastReservationsLimit).map((res) => (
                    <React.Fragment key={res.id}>
                      <TableRow hover sx={{ '& > *': { borderBottom: 'unset' } }}>
                        <TableCell>
                          <IconButton
                            size="small"
                            onClick={() => toggleReservationExpanded(res.id)}
                          >
                            {expandedReservations.has(res.id) ? (
                              <KeyboardArrowUpIcon />
                            ) : (
                              <KeyboardArrowDownIcon />
                            )}
                          </IconButton>
                        </TableCell>
                        <TableCell>{res.court?.name || 'Unknown'}</TableCell>
                        <TableCell>{courtService.formatDate(res.start_time)}</TableCell>
                        <TableCell>
                          {courtService.formatTime(res.start_time)} -{' '}
                          {courtService.formatTime(res.end_time)}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={res.reservation_type?.replace('_', ' ') || 'Singles'}
                            size="small"
                            color={res.reservation_type === 'ball_machine' ? 'secondary' : 'default'}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={res.status}
                            size="small"
                            color={
                              res.status === ReservationStatus.COMPLETED
                                ? 'success'
                                : res.status === ReservationStatus.CANCELLED
                                ? 'error'
                                : 'default'
                            }
                          />
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
                          <Collapse in={expandedReservations.has(res.id)} timeout="auto" unmountOnExit>
                            <Box sx={{ margin: 2 }}>
                              <Typography variant="subtitle2" gutterBottom>
                                Reservation Details
                              </Typography>
                              <Grid container spacing={2}>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                  <Typography variant="body2" color="text.secondary">
                                    <strong>Players:</strong>
                                  </Typography>
                                  {res.players && res.players.length > 0 ? (
                                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                                      {res.players.map((player, index) => (
                                        <Chip
                                          key={index}
                                          label={player.name}
                                          size="small"
                                          color={player.type === 'member' ? 'primary' : 'secondary'}
                                          variant="outlined"
                                        />
                                      ))}
                                    </Stack>
                                  ) : (
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                      No players added
                                    </Typography>
                                  )}
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                  <Typography variant="body2" color="text.secondary">
                                    <strong>Notes:</strong>
                                  </Typography>
                                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                                    {res.notes || 'No notes'}
                                  </Typography>
                                </Grid>
                              </Grid>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Load More / Show Less buttons */}
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2 }}>
                {pastReservationsLimit > PAST_RESERVATIONS_PAGE_SIZE && (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setPastReservationsLimit(PAST_RESERVATIONS_PAGE_SIZE)}
                  >
                    Show Less
                  </Button>
                )}
                {pastReservationsLimit < getPastReservations().length && (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setPastReservationsLimit((prev) => prev + PAST_RESERVATIONS_PAGE_SIZE)}
                  >
                    Load More
                  </Button>
                )}
              </Box>
            </>
          )}
        </>
      )}

      {/* Book a Pro Tab */}
      {tabValue === 2 && (
        <>
          <Grid container spacing={3}>
            {/* Pro Selection */}
            <Grid size={{ xs: 12, md: 4 }}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    <PersonIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Select a Pro
                  </Typography>

                  {loadingPros ? (
                    <Box display="flex" justifyContent="center" py={2}>
                      <CircularProgress />
                    </Box>
                  ) : pros.length === 0 ? (
                    <Alert severity="info">No pros available at this time.</Alert>
                  ) : (
                    <List>
                      {pros.map((pro) => (
                        <ListItem
                          key={pro.id}
                          component="div"
                          sx={{
                            cursor: 'pointer',
                            bgcolor: selectedPro?.id === pro.id ? 'action.selected' : 'inherit',
                            '&:hover': { bgcolor: 'action.hover' },
                            borderRadius: 1,
                            mb: 1,
                          }}
                          onClick={() => handleSelectPro(pro)}
                        >
                          <ListItemText
                            primary={`${pro.first_name} ${pro.last_name}`}
                            secondary={pro.departments?.join(', ') || 'Tennis Pro'}
                          />
                        </ListItem>
                      ))}
                    </List>
                  )}
                </CardContent>
              </Card>

              {/* My Pro Bookings */}
              {getUpcomingProBookings().length > 0 && (
                <Card sx={{ mt: 2 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      My Upcoming Lessons
                    </Typography>
                    <List dense>
                      {getUpcomingProBookings().slice(0, 5).map((booking) => (
                        <ListItem key={booking.id} sx={{ px: 0 }}>
                          <ListItemText
                            primary={`${booking.pro?.first_name} ${booking.pro?.last_name}`}
                            secondary={`${proBookingService.formatDate(booking.start_time)} at ${proBookingService.formatTime(booking.start_time)}`}
                          />
                          <ListItemSecondaryAction>
                            <IconButton
                              edge="end"
                              size="small"
                              color="error"
                              onClick={() => handleCancelProBooking(booking.id)}
                            >
                              <CancelIcon fontSize="small" />
                            </IconButton>
                          </ListItemSecondaryAction>
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              )}
            </Grid>

            {/* Pro Details and Availability */}
            <Grid size={{ xs: 12, md: 8 }}>
              {!selectedPro ? (
                <Card>
                  <CardContent>
                    <Box display="flex" flexDirection="column" alignItems="center" py={4}>
                      <PersonIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                      <Typography color="text.secondary">
                        Select a pro to view their availability
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              ) : (
                <>
                  {/* Pro Info */}
                  <Card sx={{ mb: 2 }}>
                    <CardContent>
                      <Stack direction="row" alignItems="center" spacing={2}>
                        <Box>
                          <Typography variant="h5">
                            {selectedPro.first_name} {selectedPro.last_name}
                          </Typography>
                          <Typography color="text.secondary">
                            {selectedPro.departments?.join(' & ') || 'Tennis Pro'}
                          </Typography>
                        </Box>
                      </Stack>

                      {/* Session Types */}
                      {proSessionTypes.length > 0 && (
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="subtitle2" gutterBottom>
                            Session Types
                          </Typography>
                          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                            {proSessionTypes.map((type) => (
                              <Chip
                                key={type.id}
                                label={`${type.name} - ${proBookingService.formatPrice(type.price_member)}`}
                                color={selectedSessionType?.id === type.id ? 'primary' : 'default'}
                                variant={selectedSessionType?.id === type.id ? 'filled' : 'outlined'}
                                onClick={() => setSelectedSessionType(type)}
                                sx={{ mb: 1 }}
                              />
                            ))}
                          </Stack>
                        </Box>
                      )}
                    </CardContent>
                  </Card>

                  {/* Date Selection */}
                  <Card sx={{ mb: 2 }}>
                    <CardContent>
                      <Stack direction="row" alignItems="center" spacing={2}>
                        <Typography variant="subtitle2">Select Date:</Typography>
                        <IconButton
                          size="small"
                          onClick={() => {
                            const date = new Date(proBookingDate);
                            date.setDate(date.getDate() - 1);
                            handleProDateChange(date.toISOString().split('T')[0]);
                          }}
                        >
                          <ChevronLeftIcon />
                        </IconButton>
                        <TextField
                          type="date"
                          size="small"
                          value={proBookingDate}
                          onChange={(e) => handleProDateChange(e.target.value)}
                          inputProps={{
                            min: new Date().toISOString().split('T')[0],
                          }}
                        />
                        <IconButton
                          size="small"
                          onClick={() => {
                            const date = new Date(proBookingDate);
                            date.setDate(date.getDate() + 1);
                            handleProDateChange(date.toISOString().split('T')[0]);
                          }}
                        >
                          <ChevronRightIcon />
                        </IconButton>
                      </Stack>
                    </CardContent>
                  </Card>

                  {/* Availability Slots */}
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        <AccessTimeIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                        Available Times
                      </Typography>

                      {loadingProAvailability ? (
                        <Box display="flex" justifyContent="center" py={2}>
                          <CircularProgress />
                        </Box>
                      ) : !proAvailability || proAvailability.available_slots.length === 0 ? (
                        <Alert severity="info">
                          No available times for this date. Please try another day.
                        </Alert>
                      ) : (
                        <Grid container spacing={1}>
                          {proAvailability.available_slots.map((slot, index) => (
                            <Grid size={{ xs: 6, sm: 4, md: 3 }} key={index}>
                              <Button
                                variant={slot.available ? 'contained' : 'outlined'}
                                color={slot.available ? 'success' : 'inherit'}
                                disabled={!slot.available}
                                fullWidth
                                onClick={() => slot.available && handleOpenProBookingDialog(slot.start_time)}
                                sx={{ py: 1 }}
                              >
                                {proBookingService.formatTime(slot.start_time)}
                              </Button>
                            </Grid>
                          ))}
                        </Grid>
                      )}
                    </CardContent>
                  </Card>
                </>
              )}
            </Grid>
          </Grid>
        </>
      )}

      {/* Programs Tab */}
      {tabValue === 3 && (
        <>
          <Grid container spacing={3}>
            {/* Available Programs */}
            <Grid size={{ xs: 12, md: 8 }}>
              <Typography variant="h6" gutterBottom>
                <SchoolIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Available Programs
              </Typography>

              {loadingPrograms ? (
                <Box display="flex" justifyContent="center" py={4}>
                  <CircularProgress />
                </Box>
              ) : programs.length === 0 ? (
                <Alert severity="info">No programs available at this time.</Alert>
              ) : (
                <Grid container spacing={2}>
                  {programs.map((program) => (
                    <Grid size={{ xs: 12, sm: 6 }} key={program.id}>
                      <Card>
                        <CardContent>
                          <Typography variant="h6" gutterBottom>
                            {program.name}
                          </Typography>
                          <Chip
                            label={programService.getProgramTypeLabel(program.program_type)}
                            size="small"
                            color="primary"
                            sx={{ mb: 1 }}
                          />
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            {program.description?.substring(0, 100) || 'No description'}
                            {(program.description?.length || 0) > 100 && '...'}
                          </Typography>
                          <Stack direction="row" spacing={2} sx={{ mb: 1 }}>
                            <Typography variant="body2">
                              <AttachMoneyIcon sx={{ fontSize: 16, verticalAlign: 'middle' }} />
                              {proBookingService.formatPrice(program.price_member)}
                            </Typography>
                            <Typography variant="body2">
                              <GroupIcon sx={{ fontSize: 16, verticalAlign: 'middle' }} />
                              {program.spots_available !== undefined
                                ? `${program.spots_available} spots left`
                                : `Max ${program.max_participants}`}
                            </Typography>
                          </Stack>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {programService.formatDateRange(program.start_date, program.end_date)}
                          </Typography>
                          <Box sx={{ mt: 2 }}>
                            {isEnrolledInProgram(program.id) ? (
                              <Chip label="Enrolled" color="success" icon={<CheckCircleIcon />} />
                            ) : (
                              <Button
                                variant="contained"
                                size="small"
                                onClick={() => handleViewProgramDetails(program)}
                              >
                                View Details
                              </Button>
                            )}
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}
            </Grid>

            {/* My Enrollments */}
            <Grid size={{ xs: 12, md: 4 }}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    My Enrollments
                  </Typography>
                  {getActiveEnrollments().length === 0 ? (
                    <Typography color="text.secondary" variant="body2">
                      You are not enrolled in any programs.
                    </Typography>
                  ) : (
                    <List dense>
                      {getActiveEnrollments().map((enrollment) => (
                        <ListItem key={enrollment.id} sx={{ px: 0 }}>
                          <ListItemText
                            primary={enrollment.program?.name || 'Unknown Program'}
                            secondary={
                              <>
                                <Chip
                                  label={programService.getEnrollmentStatusLabel(enrollment.status).label}
                                  size="small"
                                  color={programService.getEnrollmentStatusLabel(enrollment.status).color as any}
                                  sx={{ mr: 1 }}
                                />
                                {enrollment.program?.start_date &&
                                  programService.formatDateRange(
                                    enrollment.program.start_date,
                                    enrollment.program.end_date
                                  )}
                              </>
                            }
                          />
                          <ListItemSecondaryAction>
                            <IconButton
                              edge="end"
                              size="small"
                              color="error"
                              onClick={() => handleCancelEnrollment(enrollment.id)}
                            >
                              <CancelIcon fontSize="small" />
                            </IconButton>
                          </ListItemSecondaryAction>
                        </ListItem>
                      ))}
                    </List>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </>
      )}

      {/* Staff Management Tab */}
      {tabValue === 4 && isStaff && (
        <>
          <Grid container spacing={3}>
            {/* Session Types Section */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Card>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h6">
                      <AccessTimeIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                      My Session Types
                    </Typography>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => setSessionTypeDialogOpen(true)}
                    >
                      Add Session Type
                    </Button>
                  </Box>

                  {loadingStaffData ? (
                    <Box display="flex" justifyContent="center" py={2}>
                      <CircularProgress />
                    </Box>
                  ) : mySessionTypes.length === 0 ? (
                    <Alert severity="info">
                      No session types configured. Add session types to allow members to book lessons with you.
                    </Alert>
                  ) : (
                    <List>
                      {mySessionTypes.map((type) => (
                        <ListItem key={type.id} sx={{ px: 0 }}>
                          <ListItemText
                            primary={type.name}
                            secondary={
                              <>
                                {type.duration_minutes} min | Max {type.max_participants} participant(s)
                                <br />
                                Member: {proBookingService.formatPrice(type.price_member)}
                                {type.price_non_member && ` | Non-Member: ${proBookingService.formatPrice(type.price_non_member)}`}
                              </>
                            }
                          />
                          <ListItemSecondaryAction>
                            <IconButton
                              size="small"
                              onClick={() => handleEditSessionType(type)}
                              title="Edit"
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteSessionType(type.id)}
                              title="Delete"
                              color="error"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </ListItemSecondaryAction>
                        </ListItem>
                      ))}
                    </List>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Programs Section */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Card>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h6">
                      <SchoolIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                      My Programs
                    </Typography>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => setProgramDialogOpen(true)}
                    >
                      Create Program
                    </Button>
                  </Box>

                  {loadingStaffData ? (
                    <Box display="flex" justifyContent="center" py={2}>
                      <CircularProgress />
                    </Box>
                  ) : staffPrograms.length === 0 ? (
                    <Alert severity="info">
                      No programs created yet. Create a program to allow members to enroll.
                    </Alert>
                  ) : (
                    <List>
                      {staffPrograms.map((program) => (
                        <ListItem key={program.id} sx={{ px: 0 }}>
                          <ListItemText
                            primary={program.name}
                            secondary={
                              <>
                                <Chip
                                  label={programService.getProgramStatusLabel(program.status).label}
                                  size="small"
                                  color={programService.getProgramStatusLabel(program.status).color as any}
                                  sx={{ mr: 1 }}
                                />
                                {programService.formatDateRange(program.start_date, program.end_date)}
                                <br />
                                {proBookingService.formatPrice(program.price_member)} | Max {program.max_participants} participants
                              </>
                            }
                          />
                          <ListItemSecondaryAction>
                            <IconButton
                              size="small"
                              onClick={() => handleEditProgram(program)}
                              title="Edit"
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteProgram(program.id)}
                              title="Cancel Program"
                              color="error"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </ListItemSecondaryAction>
                        </ListItem>
                      ))}
                    </List>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Availability Section */}
            <Grid size={{ xs: 12 }}>
              <Card>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h6">
                      <EventIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                      My Availability Schedule
                    </Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={handleOpenAvailabilityDialog}
                    >
                      Edit Availability
                    </Button>
                  </Box>
                  {myAvailability.length === 0 ? (
                    <Alert severity="info">
                      No availability set. Click "Edit Availability" to configure your schedule.
                    </Alert>
                  ) : (
                    <List dense>
                      {myAvailability
                        .sort((a, b) => a.day_of_week - b.day_of_week)
                        .map((slot, index) => (
                          <ListItem key={slot.id || index} sx={{ px: 0 }}>
                            <ListItemText
                              primary={dayLabels[slot.day_of_week]}
                              secondary={`${slot.start_time} - ${slot.end_time}`}
                            />
                          </ListItem>
                        ))}
                    </List>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </>
      )}

      {/* Booking Dialog */}
      <Dialog open={bookingDialogOpen} onClose={handleCloseBookingDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <EventIcon />
            <Typography>Book {selectedCourt?.name}</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              <strong>Date:</strong> {selectedDate && courtService.formatDate(selectedDate + 'T00:00:00')}
              <br />
              <strong>Time:</strong> {selectedTimeSlot && formatTimeSlot(selectedTimeSlot)}
              <br />
              <strong>Duration:</strong> {courtService.getDurationLabel(selectedDuration)}
            </Alert>

            {/* Reservation Type */}
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Reservation Type</InputLabel>
              <Select
                value={reservationType}
                label="Reservation Type"
                onChange={(e) => setReservationType(e.target.value as ReservationType)}
              >
                {getReservationTypeOptions().map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Player Selection */}
            <Typography variant="subtitle2" gutterBottom>
              Add Players (Optional)
            </Typography>

            {/* Selected Players */}
            {selectedPlayers.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {selectedPlayers.map((player) => (
                    <Chip
                      key={player.id}
                      label={`${player.name} (${player.type === 'member' ? 'Member' : 'Non-Member'})`}
                      onDelete={() => removePlayer(player.id)}
                      color={player.type === 'member' ? 'primary' : 'secondary'}
                      size="small"
                      sx={{ mb: 1 }}
                    />
                  ))}
                </Stack>
              </Box>
            )}

            {/* Player Search */}
            <TextField
              fullWidth
              label="Search Members / Non-Members"
              value={playerSearchQuery}
              onChange={(e) => handlePlayerSearch(e.target.value)}
              placeholder="Type a name to search..."
              sx={{ mb: 1 }}
              InputProps={{
                endAdornment: searchingPlayers ? <CircularProgress size={20} /> : null,
              }}
            />

            {/* Search Results */}
            {(memberSearchResults.length > 0 || nonMemberSearchResults.length > 0) && (
              <Paper variant="outlined" sx={{ maxHeight: 200, overflow: 'auto', mb: 2 }}>
                <List dense>
                  {memberSearchResults.length > 0 && (
                    <>
                      <ListItem>
                        <ListItemText
                          primary={<Typography variant="caption" color="text.secondary">Members</Typography>}
                        />
                      </ListItem>
                      {memberSearchResults.map((member) => (
                        <ListItem
                          key={member.id}
                          component="div"
                          sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                          onClick={() => addPlayer(member, 'member')}
                        >
                          <ListItemText
                            primary={`${member.first_name} ${member.last_name}`}
                            secondary={member.email}
                          />
                          <ListItemSecondaryAction>
                            <IconButton edge="end" size="small" onClick={() => addPlayer(member, 'member')}>
                              <PersonAddIcon fontSize="small" />
                            </IconButton>
                          </ListItemSecondaryAction>
                        </ListItem>
                      ))}
                    </>
                  )}
                  {nonMemberSearchResults.length > 0 && (
                    <>
                      {memberSearchResults.length > 0 && <Divider />}
                      <ListItem>
                        <ListItemText
                          primary={<Typography variant="caption" color="text.secondary">Non-Members</Typography>}
                        />
                      </ListItem>
                      {nonMemberSearchResults.map((nonMember) => (
                        <ListItem
                          key={nonMember.id}
                          component="div"
                          sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                          onClick={() => addPlayer(nonMember, 'non_member')}
                        >
                          <ListItemText
                            primary={`${nonMember.first_name} ${nonMember.last_name}`}
                            secondary={nonMember.email}
                          />
                          <ListItemSecondaryAction>
                            <IconButton edge="end" size="small" onClick={() => addPlayer(nonMember, 'non_member')}>
                              <PersonAddIcon fontSize="small" />
                            </IconButton>
                          </ListItemSecondaryAction>
                        </ListItem>
                      ))}
                    </>
                  )}
                </List>
              </Paper>
            )}

            <Divider sx={{ my: 2 }} />

            <TextField
              fullWidth
              label="Notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any special requests or notes"
              multiline
              rows={2}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseBookingDialog} disabled={bookingLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleCreateReservation}
            disabled={bookingLoading}
            startIcon={bookingLoading ? <CircularProgress size={20} /> : <CheckCircleIcon />}
          >
            Confirm Booking
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cancel Dialog */}
      <Dialog open={cancelDialogOpen} onClose={handleCloseCancelDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Cancel Reservation</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            Are you sure you want to cancel this reservation?
          </Typography>
          <TextField
            fullWidth
            label="Reason (optional)"
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            placeholder="Why are you cancelling?"
            multiline
            rows={2}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCancelDialog} disabled={bookingLoading}>
            Keep Reservation
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleCancelReservation}
            disabled={bookingLoading}
            startIcon={bookingLoading ? <CircularProgress size={20} /> : <CancelIcon />}
          >
            Cancel Reservation
          </Button>
        </DialogActions>
      </Dialog>

      {/* Pro Booking Dialog */}
      <Dialog open={proBookingDialogOpen} onClose={handleCloseProBookingDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <PersonIcon />
            <Typography>Book with {selectedPro?.first_name} {selectedPro?.last_name}</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              <strong>Date:</strong> {proBookingDate && courtService.formatDate(proBookingDate + 'T00:00:00')}
              <br />
              <strong>Time:</strong> {selectedProTimeSlot && proBookingService.formatTime(selectedProTimeSlot)}
              <br />
              <strong>Session:</strong> {selectedSessionType?.name} ({selectedSessionType?.duration_minutes} min)
              <br />
              <strong>Price:</strong> {selectedSessionType && proBookingService.formatPrice(selectedSessionType.price_member)}
            </Alert>

            <TextField
              fullWidth
              label="Notes (Optional)"
              value={proBookingNotes}
              onChange={(e) => setProBookingNotes(e.target.value)}
              placeholder="Any special requests or goals for your lesson"
              multiline
              rows={2}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseProBookingDialog} disabled={proBookingLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleCreateProBooking}
            disabled={proBookingLoading || !selectedSessionType}
            startIcon={proBookingLoading ? <CircularProgress size={20} /> : <CheckCircleIcon />}
          >
            Confirm Booking
          </Button>
        </DialogActions>
      </Dialog>

      {/* Program Detail Dialog */}
      <Dialog open={programDetailDialogOpen} onClose={handleCloseProgramDetails} maxWidth="md" fullWidth>
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <SchoolIcon />
            <Typography variant="h6">{selectedProgram?.name}</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          {selectedProgram && (
            <Box sx={{ pt: 1 }}>
              <Chip
                label={programService.getProgramTypeLabel(selectedProgram.program_type)}
                color="primary"
                sx={{ mb: 2 }}
              />

              <Typography variant="body1" paragraph>
                {selectedProgram.description || 'No description available.'}
              </Typography>

              <Divider sx={{ my: 2 }} />

              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Instructor
                  </Typography>
                  <Typography>
                    {selectedProgram.instructor?.first_name} {selectedProgram.instructor?.last_name}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Dates
                  </Typography>
                  <Typography>
                    {programService.formatDateRange(selectedProgram.start_date, selectedProgram.end_date)}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Price (Member)
                  </Typography>
                  <Typography>
                    {proBookingService.formatPrice(selectedProgram.price_member)}
                  </Typography>
                </Grid>
                {selectedProgram.price_non_member && (
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Price (Non-Member)
                    </Typography>
                    <Typography>
                      {proBookingService.formatPrice(selectedProgram.price_non_member)}
                    </Typography>
                  </Grid>
                )}
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Capacity
                  </Typography>
                  <Typography>
                    {selectedProgram.spots_available !== undefined
                      ? `${selectedProgram.spots_available} of ${selectedProgram.max_participants} spots available`
                      : `Max ${selectedProgram.max_participants} participants`}
                  </Typography>
                </Grid>
                {selectedProgram.recurrence_pattern && (
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Schedule
                    </Typography>
                    <Typography>
                      {programService.formatRecurrencePattern(selectedProgram.recurrence_pattern)}
                    </Typography>
                  </Grid>
                )}
              </Grid>

              {selectedProgram.registration_deadline && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  Registration deadline: {programService.formatDate(selectedProgram.registration_deadline)}
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseProgramDetails}>
            Close
          </Button>
          {selectedProgram && !isEnrolledInProgram(selectedProgram.id) && (
            <Button
              variant="contained"
              color="primary"
              onClick={() => handleEnrollInProgram(selectedProgram.id)}
              disabled={enrollingInProgram}
              startIcon={enrollingInProgram ? <CircularProgress size={20} /> : <CheckCircleIcon />}
            >
              Enroll Now
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Session Type Dialog */}
      <Dialog open={sessionTypeDialogOpen} onClose={() => {
        setSessionTypeDialogOpen(false);
        setEditingSessionType(null);
        setSessionTypeForm({
          name: '',
          description: '',
          duration_minutes: 60,
          max_participants: 1,
          price_member: 0,
          price_non_member: 0,
        });
      }} maxWidth="sm" fullWidth>
        <DialogTitle>{editingSessionType ? 'Edit Session Type' : 'Add Session Type'}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              fullWidth
              label="Session Name"
              value={sessionTypeForm.name}
              onChange={(e) => setSessionTypeForm({ ...sessionTypeForm, name: e.target.value })}
              placeholder="e.g., Private Lesson, Group Clinic"
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Description"
              value={sessionTypeForm.description}
              onChange={(e) => setSessionTypeForm({ ...sessionTypeForm, description: e.target.value })}
              placeholder="Describe what this session includes"
              multiline
              rows={2}
              sx={{ mb: 2 }}
            />
            <Grid container spacing={2}>
              <Grid size={{ xs: 6 }}>
                <FormControl fullWidth>
                  <InputLabel>Duration</InputLabel>
                  <Select
                    value={sessionTypeForm.duration_minutes}
                    label="Duration"
                    onChange={(e) => setSessionTypeForm({ ...sessionTypeForm, duration_minutes: Number(e.target.value) })}
                  >
                    <MenuItem value={30}>30 minutes</MenuItem>
                    <MenuItem value={60}>1 hour</MenuItem>
                    <MenuItem value={90}>1.5 hours</MenuItem>
                    <MenuItem value={120}>2 hours</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 6 }}>
                <TextField
                  fullWidth
                  label="Max Participants"
                  type="number"
                  value={sessionTypeForm.max_participants}
                  onChange={(e) => setSessionTypeForm({ ...sessionTypeForm, max_participants: Number(e.target.value) })}
                  inputProps={{ min: 1, max: 20 }}
                />
              </Grid>
              <Grid size={{ xs: 6 }}>
                <TextField
                  fullWidth
                  label="Member Price ($)"
                  type="number"
                  value={sessionTypeForm.price_member}
                  onChange={(e) => setSessionTypeForm({ ...sessionTypeForm, price_member: Number(e.target.value) })}
                  inputProps={{ min: 0, step: 5 }}
                />
              </Grid>
              <Grid size={{ xs: 6 }}>
                <TextField
                  fullWidth
                  label="Non-Member Price ($)"
                  type="number"
                  value={sessionTypeForm.price_non_member}
                  onChange={(e) => setSessionTypeForm({ ...sessionTypeForm, price_non_member: Number(e.target.value) })}
                  inputProps={{ min: 0, step: 5 }}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setSessionTypeDialogOpen(false);
            setEditingSessionType(null);
            setSessionTypeForm({
              name: '',
              description: '',
              duration_minutes: 60,
              max_participants: 1,
              price_member: 0,
              price_non_member: 0,
            });
          }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreateSessionType}
            disabled={!sessionTypeForm.name || sessionTypeForm.price_member <= 0}
          >
            {editingSessionType ? 'Save Changes' : 'Create Session Type'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Program Creation Dialog */}
      <Dialog open={programDialogOpen} onClose={() => {
        setProgramDialogOpen(false);
        setEditingProgram(null);
        setProgramForm({
          name: '',
          description: '',
          program_type: 'tennis',
          price_member: 0,
          price_non_member: 0,
          max_participants: 10,
          min_participants: 4,
          start_date: '',
          end_date: '',
          recurrence_days: [],
          recurrence_time: '09:00',
          recurrence_duration: 60,
        });
      }} maxWidth="md" fullWidth>
        <DialogTitle>{editingProgram ? 'Edit Program' : 'Create New Program'}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 8 }}>
                <TextField
                  fullWidth
                  label="Program Name"
                  value={programForm.name}
                  onChange={(e) => setProgramForm({ ...programForm, name: e.target.value })}
                  placeholder="e.g., Beginner Tennis Clinic"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <FormControl fullWidth>
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={programForm.program_type}
                    label="Type"
                    onChange={(e) => setProgramForm({ ...programForm, program_type: e.target.value })}
                  >
                    <MenuItem value="tennis">Tennis</MenuItem>
                    <MenuItem value="pickleball">Pickleball</MenuItem>
                    <MenuItem value="fitness">Fitness</MenuItem>
                    <MenuItem value="swimming">Swimming</MenuItem>
                    <MenuItem value="other">Other</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="Description"
                  value={programForm.description}
                  onChange={(e) => setProgramForm({ ...programForm, description: e.target.value })}
                  placeholder="Describe what participants will learn"
                  multiline
                  rows={3}
                />
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                <TextField
                  fullWidth
                  label="Start Date"
                  type="date"
                  value={programForm.start_date}
                  onChange={(e) => setProgramForm({ ...programForm, start_date: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                <TextField
                  fullWidth
                  label="End Date"
                  type="date"
                  value={programForm.end_date}
                  onChange={(e) => setProgramForm({ ...programForm, end_date: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                <TextField
                  fullWidth
                  label="Member Price ($)"
                  type="number"
                  value={programForm.price_member}
                  onChange={(e) => setProgramForm({ ...programForm, price_member: Number(e.target.value) })}
                  inputProps={{ min: 0, step: 10 }}
                />
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                <TextField
                  fullWidth
                  label="Non-Member Price ($)"
                  type="number"
                  value={programForm.price_non_member}
                  onChange={(e) => setProgramForm({ ...programForm, price_non_member: Number(e.target.value) })}
                  inputProps={{ min: 0, step: 10 }}
                />
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                <TextField
                  fullWidth
                  label="Max Participants"
                  type="number"
                  value={programForm.max_participants}
                  onChange={(e) => setProgramForm({ ...programForm, max_participants: Number(e.target.value) })}
                  inputProps={{ min: 1 }}
                />
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                <TextField
                  fullWidth
                  label="Min Participants"
                  type="number"
                  value={programForm.min_participants}
                  onChange={(e) => setProgramForm({ ...programForm, min_participants: Number(e.target.value) })}
                  inputProps={{ min: 0 }}
                />
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                <TextField
                  fullWidth
                  label="Session Time"
                  type="time"
                  value={programForm.recurrence_time}
                  onChange={(e) => setProgramForm({ ...programForm, recurrence_time: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                <FormControl fullWidth>
                  <InputLabel>Duration</InputLabel>
                  <Select
                    value={programForm.recurrence_duration}
                    label="Duration"
                    onChange={(e) => setProgramForm({ ...programForm, recurrence_duration: Number(e.target.value) })}
                  >
                    <MenuItem value={30}>30 min</MenuItem>
                    <MenuItem value={60}>1 hour</MenuItem>
                    <MenuItem value={90}>1.5 hours</MenuItem>
                    <MenuItem value={120}>2 hours</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Select Days of Week
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {dayLabels.map((day, index) => (
                    <Chip
                      key={day}
                      label={day}
                      color={programForm.recurrence_days.includes(index) ? 'primary' : 'default'}
                      variant={programForm.recurrence_days.includes(index) ? 'filled' : 'outlined'}
                      onClick={() => {
                        const days = programForm.recurrence_days.includes(index)
                          ? programForm.recurrence_days.filter((d) => d !== index)
                          : [...programForm.recurrence_days, index].sort();
                        setProgramForm({ ...programForm, recurrence_days: days });
                      }}
                      sx={{ mb: 1 }}
                    />
                  ))}
                </Stack>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setProgramDialogOpen(false);
            setEditingProgram(null);
            setProgramForm({
              name: '',
              description: '',
              program_type: 'tennis',
              price_member: 0,
              price_non_member: 0,
              max_participants: 10,
              min_participants: 4,
              start_date: '',
              end_date: '',
              recurrence_days: [],
              recurrence_time: '09:00',
              recurrence_duration: 60,
            });
          }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreateProgram}
            disabled={!programForm.name || !programForm.start_date || !programForm.end_date || programForm.price_member <= 0}
          >
            {editingProgram ? 'Save Changes' : 'Create Program'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Availability Dialog */}
      <Dialog open={availabilityDialogOpen} onClose={() => setAvailabilityDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Your Availability</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              Set your weekly availability. Members will be able to book lessons during these times.
            </Alert>

            {/* Current slots */}
            {availabilitySlots.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Current Availability</Typography>
                <List dense>
                  {availabilitySlots
                    .sort((a, b) => a.day_of_week - b.day_of_week)
                    .map((slot, index) => (
                      <ListItem key={slot.id || index} sx={{ bgcolor: 'grey.50', mb: 0.5, borderRadius: 1 }}>
                        <ListItemText
                          primary={dayLabels[slot.day_of_week]}
                          secondary={`${slot.start_time} - ${slot.end_time}`}
                        />
                        <ListItemSecondaryAction>
                          <IconButton
                            size="small"
                            onClick={() => handleRemoveAvailabilitySlot(index)}
                            color="error"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                </List>
              </Box>
            )}

            {/* Add new slot */}
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Add Time Slot</Typography>
            <Grid container spacing={2} alignItems="center">
              <Grid size={{ xs: 4 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Day</InputLabel>
                  <Select
                    value={newSlot.day_of_week}
                    label="Day"
                    onChange={(e) => setNewSlot({ ...newSlot, day_of_week: Number(e.target.value) })}
                  >
                    {dayLabels.map((day, index) => (
                      <MenuItem key={index} value={index}>{day}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 3 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Start"
                  type="time"
                  value={newSlot.start_time}
                  onChange={(e) => setNewSlot({ ...newSlot, start_time: e.target.value })}
                  slotProps={{ inputLabel: { shrink: true } }}
                />
              </Grid>
              <Grid size={{ xs: 3 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="End"
                  type="time"
                  value={newSlot.end_time}
                  onChange={(e) => setNewSlot({ ...newSlot, end_time: e.target.value })}
                  slotProps={{ inputLabel: { shrink: true } }}
                />
              </Grid>
              <Grid size={{ xs: 2 }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleAddAvailabilitySlot}
                  disabled={!newSlot.start_time || !newSlot.end_time}
                >
                  Add
                </Button>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAvailabilityDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveAvailability}>
            Save Availability
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default CourtBookingPage;
